import { useEffect, useMemo, useRef, useState } from "react"
import { parseProfileEvent, placeholderProfile } from "@/nostr/api/profiles"
import type { NostrEvent, Profile } from "@/nostr/types"
import { KINDS } from "@/nostr/types"
import { useRelays } from "@/state/RelayContext"
import { useEventVersion, useNostr } from "@/state/NostrContext"
import { preferredLookupRelays } from "@/utils/url"

/**
 * Batches profile requests: every pubkey is requested once, in small groups,
 * so a feed with 40 authors does not open 40 subscriptions.
 */
export function useProfiles(pubkeys: string[]): Map<string, Profile> {
  const { pool, store } = useNostr()
  const { appRelays } = useRelays()
  const version = useEventVersion(store)
  const requested = useRef(new Set<string>())
  const [requestedVersion, setRequestedVersion] = useState(0)

  const unique = useMemo(() => {
    const seen = new Set<string>()
    const result: string[] = []
    for (const pubkey of pubkeys) {
      if (!pubkey || seen.has(pubkey)) continue
      seen.add(pubkey)
      result.push(pubkey)
    }
    return result
  }, [pubkeys.join(",")])

  useEffect(() => {
    const missing = unique.filter((pubkey) => !requested.current.has(pubkey))
    if (missing.length === 0) return
    for (const pubkey of missing) requested.current.add(pubkey)

    const cleanups: Array<() => void> = []
    const timer = setTimeout(() => {
      // Query-shaped: close after EOSE. The results live in the event store, so
      // keeping one subscription per profile batch would only burn REQ slots.
      let subscription: { close: () => void } | undefined
      let done = false
      subscription = pool.subscribe(
        { kinds: [KINDS.PROFILE], authors: missing, limit: missing.length },
        {
          relays: preferredLookupRelays(appRelays),
          onEvent: (event) => store.add(event),
          onEose: () => {
            if (done) return
            done = true
            subscription?.close()
            setRequestedVersion((value) => value + 1)
          },
        },
      )
      cleanups.push(() => subscription?.close())
    }, 250)

    return () => {
      clearTimeout(timer)
      for (const close of cleanups) close()
    }
  }, [unique, pool, store, appRelays])

  return useMemo(() => {
    const profiles = new Map<string, Profile>()
    for (const pubkey of unique) {
      const event = store.getReplaceable(KINDS.PROFILE, pubkey)
      profiles.set(pubkey, parseProfileEvent(event) ?? placeholderProfile(pubkey))
    }
    return profiles
    // store version and requestedVersion are the reactive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unique, version, requestedVersion, store])
}

export function useProfile(pubkey?: string): Profile | undefined {
  const profiles = useProfiles(pubkey ? [pubkey] : [])
  return pubkey ? profiles.get(pubkey) : undefined
}

/**
 * Raw kind 0 event for a pubkey (already fetched by `useProfile`/`useProfiles`).
 * Used by the profile editor to preserve fields the UI does not edit.
 */
export function useProfileEvent(pubkey?: string): NostrEvent | undefined {
  const { store } = useNostr()
  const version = useEventVersion(store)
  return useMemo(() => {
    if (!pubkey) return undefined
    return store.getReplaceable(KINDS.PROFILE, pubkey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubkey, version, store])
}

/**
 * Reads a profile that is already in the event store without requesting it.
 * Used where a lookup would be redundant (e.g. the parent of a rendered reply,
 * whose card already fetched that author) and would only burn REQ slots.
 */
export function useCachedProfile(pubkey?: string): Profile | undefined {
  const { store } = useNostr()
  const version = useEventVersion(store)
  return useMemo(() => {
    if (!pubkey) return undefined
    return parseProfileEvent(store.getReplaceable(KINDS.PROFILE, pubkey))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubkey, version, store])
}

/**
 * Resolves events by id, requesting the ones that are missing from the store.
 * Used for reposts, quotes, notification targets and thread ancestors.
 */
export function useEventsByIds(ids: string[]): Map<string, NostrEvent | undefined> {
  const { pool, store } = useNostr()
  const { appRelays } = useRelays()
  const version = useEventVersion(store)
  const requested = useRef(new Set<string>())

  const unique = useMemo(() => {
    const seen = new Set<string>()
    const result: string[] = []
    for (const id of ids) {
      if (!id || seen.has(id)) continue
      seen.add(id)
      result.push(id)
    }
    return result
  }, [ids.join(",")])

  useEffect(() => {
    const missing = unique.filter((id) => !store.has(id) && !requested.current.has(id))
    if (missing.length === 0) return
    for (const id of missing) requested.current.add(id)
    let subscription: { close: () => void } | undefined
    subscription = pool.subscribe(
      { ids: missing.slice(0, 100) },
      {
        relays: preferredLookupRelays(appRelays),
        onEvent: (event) => store.add(event),
        // By-id lookups are query-shaped; close them once the relays are done.
        onEose: () => subscription?.close(),
      },
    )
    return () => subscription?.close()
  }, [unique, pool, store, appRelays])

  return useMemo(() => {
    const map = new Map<string, NostrEvent | undefined>()
    for (const id of unique) map.set(id, store.get(id))
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unique, version, store])
}

export function useEventById(id?: string): NostrEvent | undefined {
  const events = useEventsByIds(id ? [id] : [])
  return id ? events.get(id) : undefined
}
